import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  ShieldCheck,
  Clock,
  Gem,
  EyeOff,
  UserRound,
  Sparkles,
  Building2,
  Mail,
} from "lucide-react";
import { Reveal, Eyebrow, KineticLines } from "@/components/Kinetic";
import LimoWidget from "@/components/LimoWidget";
import { SERVICES, SITE, IMG } from "@/data/site";

const scrollToId = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const PILLARS = [
  { icon: ShieldCheck, title: "Professionalism", desc: "Licensed, insured and impeccably presented chauffeurs who treat every ride as a reflection of your standards." },
  { icon: Gem, title: "Comfort", desc: "Spotless, late-model luxury SUVs with leather cabins, climate control and a genuinely quiet ride." },
  { icon: Clock, title: "Reliability", desc: "Early arrivals, flight-aware scheduling and around-the-clock dispatch you can plan your day around." },
  { icon: EyeOff, title: "Discretion", desc: "Privacy is the default. What happens in the cabin stays in the cabin — always." },
  { icon: UserRound, title: "Personalized Service", desc: "Preferred routes, cabin temperature, water, playlists — remembered and ready before you ask." },
  { icon: Sparkles, title: "Attention to Detail", desc: "The small things, handled — so the whole journey simply feels effortless." },
];

export default function Home() {
  return (
    <main data-testid="page-home">
      {/* ---------- 1 · CINEMATIC HERO ---------- */}
      <section
        className="relative flex min-h-[100svh] items-end overflow-hidden"
        data-testid="home-hero"
      >
        <picture className="absolute inset-0 h-full w-full">
          <source media="(max-width: 767px)" srcSet={IMG.heroMobile} />
          <img
            src={IMG.heroDesktop}
            alt="Black Cadillac Escalade on a private airport tarmac at night with a private jet in the background"
            className="h-full w-full object-cover"
            data-testid="home-hero-image"
          />
        </picture>
        {/* legibility scrims */}
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian-900 via-obsidian-900/55 to-obsidian-900/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian-900/85 via-obsidian-900/30 to-transparent" />

        <div className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-32 sm:px-8 lg:px-12 lg:pb-28">
          <Reveal>
            <Eyebrow>Nanak Chauffeurs · Luxury Ground Transportation</Eyebrow>
          </Reveal>
          <KineticLines
            className="mt-6 max-w-4xl font-display text-[clamp(2.6rem,6.4vw,5rem)] font-bold uppercase leading-[1.02] tracking-tight text-ivory [text-shadow:0_2px_36px_rgba(0,0,0,0.65)]"
            lines={[
              "The Standard of",
              <span key="l2">
                Luxury <em className="gold-text not-italic">Transportation</em>
              </span>,
            ]}
            delay={0.2}
          />
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-xl text-lg font-medium leading-relaxed text-ivory/90 sm:text-xl">
              Experience exceptional comfort, professionalism, and sophistication
              with Nanak Chauffeurs.
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#reservations"
                onClick={scrollToId("reservations")}
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-obsidian-900 shadow-[0_0_24px_rgba(212,175,55,0.35)] transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_44px_rgba(212,175,55,0.5)]"
              >
                Book Your Ride
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#services"
                onClick={scrollToId("services")}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 rounded-full border border-ivory/25 px-9 py-4 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-ivory transition-all duration-300 hover:border-gold hover:text-gold"
              >
                Explore Our Services
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- 2 · INTRO ---------- */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-12 lg:py-32" data-testid="home-intro">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Eyebrow>Luxury Ground Transportation</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-semibold leading-tight text-ivory sm:text-5xl">
              Composure, from the curb to the <em className="gold-text italic">destination.</em>
            </h2>
            <p className="mt-7 max-w-xl text-lg font-medium leading-relaxed text-silver">
              Nanak Chauffeurs is a premium, professionally operated ground
              transportation service. We pair newest-generation black SUVs with
              chauffeurs chosen for discretion and polish — so every arrival is
              calm, punctual and quietly impressive.
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-silver/80">
              No surge pricing, no uncertainty. Just a reserved car, a
              professional at the wheel, and a journey engineered around your
              schedule.
            </p>
            <Link
              to="/about"
              data-testid="home-intro-about-link"
              className="group mt-9 inline-flex items-center gap-2 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-gold transition-colors hover:text-gold-light"
            >
              The Nanak difference
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="gold-frame overflow-hidden rounded-3xl">
              <img
                src={IMG.escaladeHotel}
                alt="Black Cadillac Escalade at an upscale hotel entrance at night"
                className="aspect-[4/3] w-full object-cover"
                data-testid="home-intro-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- 3 · SERVICES ---------- */}
      <section id="services" className="scroll-mt-24 border-t border-gold/10 bg-obsidian-800/40" data-testid="home-services">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="max-w-2xl">
            <Eyebrow>Our Services</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-semibold leading-tight text-ivory sm:text-5xl">
              One standard, every <em className="gold-text italic">occasion.</em>
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <article
                  data-testid={`home-service-card-${s.id}`}
                  className="group relative h-full overflow-hidden rounded-2xl border border-gold/15 bg-obsidian-700 transition-all duration-300 hover:-translate-y-1 hover:border-gold/45 hover:shadow-[0_18px_40px_rgba(212,175,55,0.12)]"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian-700 via-obsidian-700/30 to-transparent" />
                    <span className="absolute left-5 top-5 font-mono text-xs tracking-[0.3em] text-gold">
                      {s.no}
                    </span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-2xl font-semibold text-ivory">{s.title}</h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-silver/85">{s.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
            <Reveal delay={0.16}>
              <Link
                to="/services"
                data-testid="home-services-all-link"
                className="group flex h-full min-h-[240px] flex-col justify-between rounded-2xl border border-dashed border-gold/30 bg-transparent p-7 transition-colors duration-300 hover:border-gold/60 hover:bg-obsidian-700"
              >
                <span className="font-display text-2xl font-semibold text-ivory">
                  Every detail, <em className="gold-text italic">arranged.</em>
                </span>
                <span className="inline-flex items-center gap-2 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-gold">
                  View all services
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- 4 · THE EXPERIENCE ---------- */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-12 lg:py-32" data-testid="home-experience">
        <Reveal className="max-w-2xl">
          <Eyebrow>The Nanak Chauffeurs Experience</Eyebrow>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-tight text-ivory sm:text-5xl">
            Why discerning riders <em className="gold-text italic">stay.</em>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-gold/15 bg-gold/10 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map(({ icon: Icon, title, desc }, i) => (
            <Reveal key={title} delay={(i % 3) * 0.07}>
              <div className="h-full bg-obsidian-800 p-8 transition-colors duration-300 hover:bg-obsidian-700">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-gold/30 text-gold">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-semibold text-ivory">{title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-silver/85">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- 5 · CORPORATE TEASER ---------- */}
      <section className="relative overflow-hidden" data-testid="home-corporate">
        <img
          src={IMG.corporate}
          alt="Corporate executive transportation"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian-900 via-obsidian-900/85 to-obsidian-900/55" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="max-w-2xl">
            <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
              <Building2 className="h-4 w-4" /> Corporate Transportation
            </span>
            <h2 className="mt-6 font-display text-4xl font-semibold leading-tight text-ivory sm:text-5xl">
              A dedicated portal for <em className="gold-text italic">corporate partners.</em>
            </h2>
            <p className="mt-6 max-w-xl text-lg font-medium leading-relaxed text-silver">
              Approved corporate partners will soon monitor reservations, view
              trip details and access the latest available statuses — all in one
              secure dashboard, styled to the Nanak standard.
            </p>
            <Link
              to="/corporate-login"
              data-testid="corporate-teaser-login-btn"
              className="group mt-9 inline-flex items-center gap-2 rounded-full border border-gold/50 px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian-900"
            >
              Corporate Client Login
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- 6 · RESERVATIONS ---------- */}
      <section id="reservations" className="scroll-mt-24 border-t border-gold/10 bg-obsidian-800/40" data-testid="home-reservations">
        <div className="mx-auto max-w-6xl px-5 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal className="text-center">
            <Eyebrow>Reservations · Powered by LimoAnywhere</Eyebrow>
            <h2 className="mx-auto mt-6 max-w-2xl font-display text-4xl font-semibold leading-tight text-ivory sm:text-5xl">
              Reserve your <em className="gold-text italic">chauffeur.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-silver/85">
              Enter your trip below for an instant quote and confirmation — or
              open the full booking engine in its own page.
            </p>
          </Reveal>
          <Reveal className="mt-12">
            <LimoWidget type="quickres" minHeight={440} testId="home-quickres-widget" />
          </Reveal>
          <Reveal className="mt-6 text-center">
            <Link
              to="/reservation"
              data-testid="home-reservations-full-link"
              className="inline-flex items-center gap-2 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-gold transition-colors hover:text-gold-light"
            >
              Open the full booking engine
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- 7 · CONTACT TEASER ---------- */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-12 lg:py-28" data-testid="home-contact">
        <div className="gold-frame flex flex-col items-start justify-between gap-8 rounded-3xl bg-obsidian-800 p-10 sm:p-14 md:flex-row md:items-center">
          <div>
            <Eyebrow>Contact</Eyebrow>
            <h2 className="mt-5 max-w-xl font-display text-3xl font-semibold leading-tight text-ivory sm:text-4xl">
              Planning something that deserves the <em className="gold-text italic">right car?</em>
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="home-contact-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-obsidian-900 transition-all duration-300 hover:bg-gold-light"
            >
              Contact Dispatch
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="home-contact-email"
              className="inline-flex items-center gap-2 font-sans text-sm font-medium text-silver transition-colors hover:text-gold"
            >
              <Mail className="h-4 w-4 text-gold" /> {SITE.email}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
